import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import { useNote } from '../context/NoteContext';
import { copyToClipboard, saveAsTextFile, downloadAsPdf, copyPlainText } from '../utils/bottomRightUtils';
import ProcessModal from './ProcessModal';
import TemplateModal from './TemplateModal';
import ReactMarkdown from 'react-markdown';
import '../css/BottomRightTextViewer.css';
import { addDocumentNumbering, formatTranscriptForDisplay } from '../utils/docViewUtils';

function TextViewer() {
  const { t } = useTranslation();
  const { currentSessionId, setCurrentSessionId, allNotes } = useNote();
  const [editableNote, setEditableNote] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [sessionDocuments, setSessionDocuments] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [processingJobs, setProcessingJobs] = useState([]);
  const [processingErrorSession, setProcessingErrorSession] = useState(null);
  const nextProcessingJob = useRef(0);
  const textareaRef = useRef(null);
  const [isUserClicking, setIsUserClicking] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  // The document list owns the subscription for every session, including
  // manually generated documents.
  // This viewer consumes the shared cache.

  useEffect(() => {
    setIsProcessModalOpen(false);
  }, [currentSessionId]);

  // This useEffect correctly handles displaying the text for the active tab
  // or defaulting to the first tab if the active one is deleted.
 useEffect(() => {
  if (isUserClicking) { // ADD THIS CHECK
    console.log('*** useEffect SKIPPED (user is clicking) ***');
    return;
  }



  try {
    if (sessionDocuments.length > 0) {

      const activeDoc = sessionDocuments.find(doc => doc.id === activeNoteId);


      if (!activeDoc) {

        const defaultDoc = sessionDocuments[0];

        setActiveNoteId(defaultDoc.id);

        setEditableNote(defaultDoc.note);
      }
    } else {

      setEditableNote('');
      setActiveNoteId(null);
    }
  } catch (error) {
    console.log('ERROR in useEffect:', error);
  }

}, [activeNoteId, sessionDocuments, isUserClicking]); // ADD isUserClicking to dependencies

  useEffect(() => {
    if (!currentSessionId) {
      setSessionDocuments(previous => previous.length === 0 ? previous : []);
      return;
    }

    let sessionNotes = allNotes
      .filter(note => note.session_id === currentSessionId)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const wasOnlyOneNote = sessionDocuments.length === 1;
    const nowHasTwoNotes = sessionNotes.length === 2;
    const shouldAutoSwitch = wasOnlyOneNote && nowHasTwoNotes;

    if (sessionNotes.length >= 2) {
      sessionNotes = [sessionNotes[1], sessionNotes[0], ...sessionNotes.slice(2)];
    }

    if (shouldAutoSwitch) {
      const newestNote = sessionNotes[0];
      setActiveNoteId(newestNote.id);
      setEditableNote(newestNote.note || '');
    }

    const currentDocIds = sessionDocuments.map(doc => doc.id);
    const newDocIds = sessionNotes.map(doc => doc.id);
    const areDifferent = currentDocIds.length !== newDocIds.length || currentDocIds.join(',') !== newDocIds.join(',');

    if (!areDifferent) {
      return;
    }

    setSessionDocuments(addDocumentNumbering(sessionNotes));
  }, [allNotes, currentSessionId, sessionDocuments]);

 const handleSessionDocClick = (note) => {

  setIsUserClicking(true); // ADD THIS LINE

  try {
    if (!note?.id) {
      console.log('6. EARLY EXIT: No note ID');
      return;
    }

    const currentNote = sessionDocuments.find(doc => doc?.id === note.id);


    if (!currentNote) {
      console.log('9. EARLY EXIT: Note not found in sessionDocuments');
      return;
    }


    setActiveNoteId(currentNote.id);


    setEditableNote(currentNote.note || '');


  } catch (error) {
    console.log('13. ERROR in handleSessionDocClick:', error);
    console.log('14. Error stack:', error.stack);
  }


  setTimeout(() => setIsUserClicking(false), 100);

};




  const handleClear = (event) => {
    setSessionDocuments([]);
    setActiveNoteId(null);
    setEditableNote('');
    setCurrentSessionId(null); // Clear the current session

    const button = event.target;
    button.classList.add('clicked');
    setTimeout(() => button.classList.remove('clicked'), 200);
  };

  const handleToggleEdit = () => { setIsEditing(prevIsEditing => !prevIsEditing); };
  const handleProcessStart = (documentTypeName) => {
    setProcessingErrorSession(null);
    const job = { id: `processing-${++nextProcessingJob.current}`, kind: documentTypeName, sessionId: currentSessionId };
    setProcessingJobs(jobs => [...jobs, job]);
    return job.id;
  };
  const handleProcessComplete = (jobId) => {
    setProcessingJobs(jobs => jobs.filter(job => job.id !== jobId));
  };
  const handleProcessError = (jobId) => {
    setProcessingErrorSession(currentSessionId);
    handleProcessComplete(jobId);
  };
  const handleTemplateModalClose = () => { setIsTemplateModalOpen(false); };


  const getCleanContent = (content) => {
    if (typeof content !== 'string') return '';

    // Check if wrapped in code block
    if (content.startsWith('```') && content.endsWith('```')) {
      const cleaned = content
        .replace(/^```[a-zA-Z]*\n?/, '') // Remove opening
        .replace(/\n?```$/, '')          // Remove closing
        .trim();
      return formatTranscriptForDisplay(cleaned, sessionDocuments.find(doc => doc.id === activeNoteId));
    }
    return formatTranscriptForDisplay(content, sessionDocuments.find(doc => doc.id === activeNoteId));
  };

  const currentProcessingJobs = processingJobs.filter(job => job.sessionId === currentSessionId);
  const isProcessDisabled = typeof editableNote !== 'string' || editableNote.trim() === '' || currentProcessingJobs.length > 0;

  return (
    <div className="text-viewer-container">
      {processingErrorSession && processingErrorSession === currentSessionId && <p role="alert">{t('process.error')}</p>}
      <div className="viewer-title-controls">
        {currentSessionId && (sessionDocuments.length > 0 || currentProcessingJobs.length > 0) ? (
          <div className="session-docs-tabs-container">
            {addDocumentNumbering(sessionDocuments).slice(0, 15).map((doc) => (
              <button
                key={doc.id}
                className={`session-doc-tab ${doc.id === activeNoteId ? 'active' : ''}`}
                onClick={() => handleSessionDocClick(doc)}
              >
                {doc.type}
              </button>
            ))}
            {currentProcessingJobs.map(job => (
              <button key={job.id} className="session-doc-tab processing" disabled>
                {t(`documents.${job.kind}`)}
                <span className="spinner" aria-hidden="true"></span>
              </button>
            ))}
            <button
              className="add-tab-button"
              onClick={() => setIsProcessModalOpen(true)}
              disabled={isProcessDisabled}
              title={t('notes.processTitle')}
              aria-label={t('notes.processTitle')}
            >
              +
            </button>
          </div>
        ) : (
          <h2 className="text-viewer-header">{t('notes.viewer')}</h2>
        )}

        <div>
          <div className="copy-button-container">
            <button onClick={(event) => copyPlainText(editableNote, event.currentTarget)} className="viewer-edit-button">{t('common.copy')}</button>
            <span id="copy-message" className="copy-message">{t('common.copied')}</span>
          </div>
          <button onClick={handleToggleEdit} className="viewer-edit-button">{isEditing ? t('common.save') : t('common.edit')}</button>
          <button onClick={handleClear} className="viewer-edit-button">{t('common.clear')}</button>
        </div>
      </div>

      <div className="text-viewer-markdown" dir="auto" lang={sessionDocuments.find(doc => doc.id === activeNoteId)?.content_language || undefined}>
        {isEditing ? (
          <textarea dir="auto" ref={textareaRef} value={editableNote} onChange={(e) => setEditableNote(e.target.value)} className="text-viewer-textarea" rows="10" autoFocus />
        ) : (
          <ReactMarkdown>{getCleanContent(editableNote)}</ReactMarkdown>
        )}
      </div>
      <div className="text-viewer-buttons">
      <button
          onClick={() => setIsTemplateModalOpen(true)}
          className="dropdown-button"
        >
          {t('notes.template')}
        </button>
        <div className="dropdown">
          <button className="dropdown-button">{t('notes.exportOptions')}</button>
          <div className="dropdown-content">
            <button onClick={() => downloadAsPdf(editableNote)}>{t('notes.downloadPdf')}</button>
            <button onClick={() => saveAsTextFile(editableNote)}>{t('notes.saveText')}</button>
            <button onClick={() => copyToClipboard(editableNote)}>{t('notes.copyMarkdown')}</button>
          </div>
        </div>
      </div>
      {isProcessModalOpen && <ProcessModal
        onClose={() => setIsProcessModalOpen(false)}
        noteContent={editableNote}
        sessionId={currentSessionId}
        onProcessStart={handleProcessStart}
        onProcessComplete={handleProcessComplete}
        onProcessError={handleProcessError}
      />}
      {isTemplateModalOpen && (
  <TemplateModal
    isOpen={isTemplateModalOpen}
    onClose={handleTemplateModalClose}
  />
)}
    </div>
  );
}

export default TextViewer;
